import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Zap, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import api, { formatApiError, apiErrorCode } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SiteFooter from "@/components/SiteFooter";
import { toast } from "sonner";

const VERIFY_MESSAGES = {
  invalid: { type: "error", text: "That verification link is invalid. Request a new one from your account settings." },
  expired: { type: "error", text: "Your verification link expired. Please request a new one from your account settings." },
  error: { type: "error", text: "Something went wrong verifying your email. Try clicking the link again — if it keeps failing, contact support@myrankpilotai.com." },
};

const HERO_BG = "https://images.unsplash.com/photo-1762279389083-abf71f22d338?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxkYXRhJTIwYW5hbHl0aWNzJTIwY2hhcnQlMjBhYnN0cmFjdHxlbnwwfHx8fDE3NzgzMTI2MjR8MA&ixlib=rb-4.1.0&q=85";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35.5 24 35.5c-6.3 0-11.5-5.2-11.5-11.5S17.7 12.5 24 12.5c2.9 0 5.5 1.1 7.5 2.9l5.7-5.7C33.5 6.4 29 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.3-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 12.5 24 12.5c2.9 0 5.5 1.1 7.5 2.9l5.7-5.7C33.5 6.4 29 4.5 24 4.5 16.3 4.5 9.7 8.7 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 43.5c5 0 9.5-1.9 12.9-5l-6-5.1c-2 1.5-4.5 2.4-6.9 2.4-5.3 0-9.7-3.1-11.3-7.5l-6.6 5.1C9.6 39.1 16.2 43.5 24 43.5z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.4l6 5.1c-.4.4 6.3-4.6 6.3-14.5 0-1.2-.1-2.3-.4-3.5z"/>
    </svg>
  );
}

export function LoginForm() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const initialEmail = (() => {
    try {
      return new URLSearchParams(window.location.search).get("email") || "";
    } catch { return ""; }
  })();
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [errCode, setErrCode] = useState("");
  const [loading, setLoading] = useState(false);

  // Show a toast when the email-verify backend redirects here with ?email_verified=...
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const status = params.get("email_verified");
      if (status && VERIFY_MESSAGES[status]) {
        toast.error(VERIFY_MESSAGES[status].text, { duration: 7000 });
        params.delete("email_verified");
        const newSearch = params.toString();
        window.history.replaceState(null, "", window.location.pathname + (newSearch ? `?${newSearch}` : ""));
      }
    } catch { /* ignore */ }
  }, []);


  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setErrCode("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      navigate("/dashboard", { replace: true });
    } catch (e2) {
      setErr(formatApiError(e2));
      setErrCode(apiErrorCode(e2) || "");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Log in to your RankPilot AI workspace." testid="login">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div>
          <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Email</Label>
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                 className="h-11 bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-[#0055FF]"
                 data-testid="login-email" />
        </div>
        <div>
          <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Password</Label>
          <PasswordInput required value={password} onChange={(e) => setPassword(e.target.value)}
                 className="h-11 bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-[#0055FF]"
                 data-testid="login-password" />
          <div className="mt-2 text-right">
            <Link to="/forgot-password" className="text-xs text-zinc-500 hover:text-[#3377FF] transition-colors" data-testid="link-forgot">
              Forgot password?
            </Link>
          </div>
        </div>
        {err && (
          <div className="text-sm text-red-300 border border-red-900/40 bg-red-950/30 px-3 py-2.5 rounded-sm space-y-1.5" data-testid="login-error">
            <div>{err}</div>
            {errCode === "account_not_found" && (
              <div className="text-xs text-red-200/80">
                New here?{" "}
                <Link to={`/register${email ? `?email=${encodeURIComponent(email)}` : ""}`}
                      className="text-[#3377FF] hover:text-white underline underline-offset-2 font-medium"
                      data-testid="login-error-signup-link">
                  Create an account →
                </Link>
              </div>
            )}
            {errCode === "wrong_password" && (
              <div className="text-xs text-red-200/80">
                <Link to="/forgot-password" className="text-[#3377FF] hover:text-white underline underline-offset-2 font-medium" data-testid="login-error-reset-link">
                  Reset your password →
                </Link>
              </div>
            )}
            {errCode === "google_account" && (
              <div className="text-xs text-red-200/80">
                <button type="button" onClick={googleLogin}
                        className="text-[#3377FF] hover:text-white underline underline-offset-2 font-medium"
                        data-testid="login-error-google-link">
                  Continue with Google →
                </button>
              </div>
            )}
          </div>
        )}
        <Button type="submit" disabled={loading} className="w-full h-11 bg-[#0055FF] hover:bg-[#3377FF] rounded-sm" data-testid="login-submit">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Log in"}
        </Button>
      </form>

      <Divider />

      <Button onClick={googleLogin} variant="outline"
              className="w-full h-11 bg-zinc-950 hover:bg-zinc-900 border-zinc-800 rounded-sm gap-2" data-testid="login-google">
        <GoogleIcon /> Continue with Google
      </Button>

      <p className="text-center text-sm text-zinc-500 mt-8">
        New here? <Link to="/register" className="text-[#3377FF] hover:text-white transition-colors" data-testid="link-register">Create an account</Link>
      </p>
    </AuthShell>
  );
}

export function RegisterForm() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const params = (() => {
    try { return new URLSearchParams(window.location.search); } catch { return new URLSearchParams(); }
  })();
  const initialEmail = params.get("email") || "";
  const refCode = (params.get("ref") || "").trim();
  // Persist the ref code so it survives the OAuth round-trip (Google login on the same page)
  if (refCode) {
    try { window.localStorage.setItem("rp_ref", refCode); } catch {}
    // Track click anonymously
    try {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/affiliate/track-click`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: refCode }),
      });
    } catch {}
  }
  const [name, setName] = useState("");
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [errCode, setErrCode] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setErrCode("");
    setLoading(true);
    const ref = (() => { try { return window.localStorage.getItem("rp_ref") || ""; } catch { return ""; } })();
    try {
      const { data } = await api.post("/auth/register", { email, password, name, ref: ref || undefined });
      try { window.localStorage.removeItem("rp_ref"); } catch {}
      setUser(data);
      navigate("/dashboard", { replace: true });
    } catch (e2) {
      setErr(formatApiError(e2));
      setErrCode(apiErrorCode(e2) || "");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Create account" subtitle="Start ranking on autopilot in under a minute." testid="register">
      {refCode && (
        <div className="mb-4 text-xs bg-[#0055FF]/10 border border-[#0055FF]/30 text-[#3377FF] rounded-sm px-3 py-2 flex items-center gap-2" data-testid="register-referral-banner">
          <span className="text-[10px] tracking-widest uppercase">Invited by</span>
          <code className="font-mono">{refCode}</code>
          <span className="text-zinc-400 ml-auto">20% off lifetime tracked</span>
        </div>
      )}
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div>
          <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Full name</Label>
          <Input required value={name} onChange={(e) => setName(e.target.value)}
                 className="h-11 bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-[#0055FF]"
                 data-testid="register-name" />
        </div>
        <div>
          <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Email</Label>
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                 className="h-11 bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-[#0055FF]"
                 data-testid="register-email" />
        </div>
        <div>
          <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Password</Label>
          <PasswordInput required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
                 className="h-11 bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-[#0055FF]"
                 data-testid="register-password" />
          <p className="text-xs text-zinc-600 mt-1.5">Min 6 characters.</p>
        </div>
        {err && (
          <div className="text-sm text-red-300 border border-red-900/40 bg-red-950/30 px-3 py-2.5 rounded-sm space-y-1.5" data-testid="register-error">
            <div>{err}</div>
            {errCode === "email_exists" && (
              <div className="text-xs text-red-200/80">
                <Link to={`/login${email ? `?email=${encodeURIComponent(email)}` : ""}`}
                      className="text-[#3377FF] hover:text-white underline underline-offset-2 font-medium"
                      data-testid="register-error-login-link">
                  Log in here →
                </Link>
                {" · "}
                <Link to="/forgot-password" className="text-[#3377FF] hover:text-white underline underline-offset-2 font-medium" data-testid="register-error-forgot-link">
                  Forgot password?
                </Link>
              </div>
            )}
            {errCode === "email_exists_google" && (
              <div className="text-xs text-red-200/80">
                <button type="button" onClick={googleLogin}
                        className="text-[#3377FF] hover:text-white underline underline-offset-2 font-medium"
                        data-testid="register-error-google-link">
                  Continue with Google →
                </button>
              </div>
            )}
          </div>
        )}
        <Button type="submit" disabled={loading} className="w-full h-11 bg-[#0055FF] hover:bg-[#3377FF] rounded-sm" data-testid="register-submit">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create account"}
        </Button>
      </form>

      <Divider />

      <Button onClick={googleLogin} variant="outline"
              className="w-full h-11 bg-zinc-950 hover:bg-zinc-900 border-zinc-800 rounded-sm gap-2" data-testid="register-google">
        <GoogleIcon /> Sign up with Google
      </Button>

      <p className="text-center text-sm text-zinc-500 mt-8">
        Already have an account? <Link to="/login" className="text-[#3377FF] hover:text-white transition-colors" data-testid="link-login">Log in</Link>
      </p>
    </AuthShell>
  );
}

function Divider() {
  return (
    <div className="my-6 flex items-center gap-3 text-[10px] tracking-[0.3em] text-zinc-600">
      <span className="flex-1 h-px bg-zinc-800" />OR<span className="flex-1 h-px bg-zinc-800" />
    </div>
  );
}

function AuthShell({ children, title, subtitle, testid }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#09090B]">
      {/* Left visual */}
      <div className="hidden lg:flex relative border-r border-zinc-800/80 overflow-hidden">
        <img src={HERO_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#09090B] via-[#09090B]/40 to-[#0055FF]/20" />
        <div className="grain absolute inset-0" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <Link to="/" className="flex items-center gap-2 w-fit" data-testid="auth-logo">
            <div className="w-7 h-7 bg-[#0055FF] rounded-sm grid place-items-center">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="font-display font-semibold text-lg">RankPilot AI</span>
          </Link>
          <div>
            <span className="label-eyebrow">Operator notes</span>
            <p className="font-display text-3xl mt-3 max-w-md leading-tight">
              "Switched from a 4-tool stack to RankPilot. Cancelled $400/mo in subscriptions in week one."
            </p>
            <div className="mt-6 text-sm text-zinc-500">— Maya R., growth lead at Forge Labs</div>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex flex-col justify-center px-6 py-12 lg:px-16">
        <div className="max-w-md mx-auto w-full" data-testid={`${testid}-page`}>
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-12">
            <div className="w-7 h-7 bg-[#0055FF] rounded-sm grid place-items-center">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="font-display font-semibold text-lg">RankPilot AI</span>
          </Link>
          <span className="label-eyebrow">{testid === "login" ? "Sign in" : "Get started"}</span>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-2 mb-2">{title}</h1>
          <p className="text-zinc-400 mb-8">{subtitle}</p>
          {children}
          <SiteFooter variant="minimal" />
        </div>
      </div>
    </div>
  );
}
